import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link2, ExternalLink, BookOpen, User, FileText, Beaker, Plus } from 'lucide-react';
import TimestampTooltip from './TimestampTooltip';

interface Reference {
  id: string;
  start: number;
  end: number;
  type: string;
  content: string;
  title?: string;
  url?: string;
  linkText?: string;
  category?: string;
  context?: string;
  visualSuggestion?: string;
}

interface TimelineReferencesProps {
  references: Reference[];
  duration: number;
  width: number;
  isDarkMode: boolean;
  viewportStart?: number;
  viewportEnd?: number;
  timeToX?: (time: number) => number;
  xToTime?: (x: number) => number;
  onSelectReference?: (reference: Reference) => void;
  selectedReferenceId?: string | null;
  currentTime?: number;
  onAddReference?: (timestamp: number) => void;
  canEdit?: boolean;
  // Drag functionality props
  onDragStart?: (reference: Reference) => void;
  onDragMove?: (reference: Reference, newTime: number) => void;
  onDragEnd?: (reference: Reference, newTime: number) => void;
}

const ORB_SIZE = 20;
const MIN_SPACING = 24;
const CLUSTER_PILL_WIDTH = 28;
const CLUSTER_MENU_WIDTH = 240;

interface PositionedReference {
  reference: Reference;
  x: number;
}

type ReferenceRenderItem =
  | {
      type: 'reference';
      reference: Reference;
      x: number;
    }
  | {
      type: 'cluster';
      id: string;
      references: Reference[];
      x: number;
    };

const formatReferenceTime = (seconds: number) => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Get icon based on category or URL
const getCategoryIcon = (reference: Reference) => {
  const url = reference.url?.toLowerCase() || '';
  const category = reference.category?.toLowerCase() || '';
  const title = reference.title?.toLowerCase() || '';

  if (url.includes('arxiv') || url.includes('doi.org') || category.includes('paper')) {
    return <FileText size={10} />;
  }
  if (url.includes('amazon') || category.includes('book')) {
    return <BookOpen size={10} />;
  }
  if (url.includes('youtube') || category.includes('mlst') || category.includes('video')) {
    return <ExternalLink size={10} />;
  }
  if (category.includes('person') || title?.match(/\b(Dr\.?|Prof\.?|Professor)\b/i)) {
    return <User size={10} />;
  }
  if (category.includes('research') || category.includes('experiment')) {
    return <Beaker size={10} />;
  }
  return <Link2 size={10} />;
};

const TimelineReferences: React.FC<TimelineReferencesProps> = ({
  references,
  duration,
  width,
  isDarkMode,
  viewportStart,
  viewportEnd,
  timeToX,
  xToTime,
  onSelectReference,
  selectedReferenceId,
  currentTime = 0,
  onAddReference,
  canEdit = false,
  onDragStart,
  onDragMove,
  onDragEnd
}) => {
  const [isHoveringAddButton, setIsHoveringAddButton] = useState(false);
  const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null);

  // Drag state
  const [draggingRef, setDraggingRef] = useState<Reference | null>(null);
  const [dragX, setDragX] = useState<number>(0);
  const [dragTime, setDragTime] = useState<number>(0);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ pointerId: number; refX: number } | null>(null);

  const handleReferenceSelect = useCallback((reference: Reference) => {
    setExpandedClusterId(null);
    onSelectReference?.(reference);
  }, [onSelectReference]);

  const resolveX = useCallback((time: number) => {
    if (timeToX) {
      return timeToX(time);
    }
    if (duration <= 0 || width <= 0) {
      return 0;
    }
    return (time / duration) * width;
  }, [duration, timeToX, width]);

  const resolveTimeFromX = useCallback((x: number) => {
    const clampedX = Math.max(ORB_SIZE / 2, Math.min(width - ORB_SIZE / 2, x));
    if (xToTime) {
      return xToTime(clampedX);
    }
    return Math.max(0, Math.min(duration, (clampedX / width) * duration));
  }, [duration, width, xToTime]);

  // Handle pointer move during drag
  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!draggingRef || !containerRef.current || !dragStartRef.current) return;
    if (dragStartRef.current.pointerId !== e.pointerId) return;

    const rect = containerRef.current.getBoundingClientRect();
    const pointerX = e.clientX - rect.left;
    const clampedX = Math.max(ORB_SIZE / 2, Math.min(width - ORB_SIZE / 2, pointerX));
    const newTime = resolveTimeFromX(clampedX);

    setDragX(clampedX);
    setDragTime(newTime);
    setTooltipPosition({ x: e.clientX, y: rect.top });

    onDragMove?.(draggingRef, newTime);
  }, [draggingRef, onDragMove, resolveTimeFromX, width]);

  // Handle pointer up to end drag
  const handlePointerUp = useCallback((e: PointerEvent) => {
    if (dragStartRef.current && dragStartRef.current.pointerId !== e.pointerId) {
      return;
    }
    if (draggingRef) {
      onDragEnd?.(draggingRef, dragTime);
      setDraggingRef(null);
      dragStartRef.current = null;
    }
  }, [draggingRef, dragTime, onDragEnd]);

  // Add/remove global event listeners
  useEffect(() => {
    if (draggingRef) {
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerUp);
      return () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerUp);
      };
    }
  }, [draggingRef, handlePointerMove, handlePointerUp]);

  useEffect(() => {
    if (!expandedClusterId) return;

    const handlePointerDownOutside = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setExpandedClusterId(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpandedClusterId(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDownOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [expandedClusterId]);

  // Start drag on pointer down
  const handleDragStart = (e: React.PointerEvent, reference: Reference, initialX: number) => {
    if (!canEdit) return;

    e.preventDefault();
    e.stopPropagation();

    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setDraggingRef(reference);
      setDragX(initialX);
      setDragTime(reference.start);
      setTooltipPosition({ x: e.clientX, y: rect.top });
      dragStartRef.current = { pointerId: e.pointerId, refX: initialX };
      onDragStart?.(reference);
    }
  };

  // Calculate the position for the add button (follows playhead)
  const addButtonX = useMemo(() => {
    return Math.max(ORB_SIZE / 2, Math.min(width - ORB_SIZE / 2, resolveX(currentTime)));
  }, [currentTime, resolveX]);

  const visibleReferences = useMemo(() => {
    if (!references.length || width === 0) return [];

    return [...references]
      .filter((reference) => {
        if (viewportStart === undefined || viewportEnd === undefined) {
          return true;
        }
        return reference.end >= viewportStart && reference.start <= viewportEnd;
      })
      .sort((a, b) => a.start - b.start);
  }, [references, viewportEnd, viewportStart, width]);

  // Calculate positions for reference orbs or read-only clusters
  const referenceRenderItems = useMemo<ReferenceRenderItem[]>(() => {
    if (!visibleReferences.length || width === 0) return [];

    if (canEdit) {
      const positions: PositionedReference[] = [];

      visibleReferences.forEach((reference) => {
        let x = resolveX(reference.start);

        for (let i = positions.length - 1; i >= 0; i--) {
          const prevPos = positions[i];
          if (x - prevPos.x < MIN_SPACING) {
            x = prevPos.x + MIN_SPACING;
            break;
          }
        }

        positions.push({ reference, x });
      });

      return positions.map(({ reference, x }) => ({
        type: 'reference',
        reference,
        x,
      }));
    }

    const rawPositions: PositionedReference[] = visibleReferences.map((reference) => ({
      reference,
      x: resolveX(reference.start),
    }));

    const items: ReferenceRenderItem[] = [];
    let currentCluster: PositionedReference[] = [];

    const flushCluster = () => {
      if (currentCluster.length === 0) return;

      if (currentCluster.length === 1) {
        items.push({
          type: 'reference',
          reference: currentCluster[0].reference,
          x: currentCluster[0].x,
        });
      } else {
        const averageX = currentCluster.reduce((sum, position) => sum + position.x, 0) / currentCluster.length;
        items.push({
          type: 'cluster',
          id: `cluster:${currentCluster.map(({ reference }) => reference.id).join('|')}`,
          references: currentCluster.map(({ reference }) => reference),
          x: Math.max(CLUSTER_PILL_WIDTH / 2, Math.min(width - CLUSTER_PILL_WIDTH / 2, averageX)),
        });
      }

      currentCluster = [];
    };

    rawPositions.forEach((position) => {
      if (currentCluster.length === 0) {
        currentCluster = [position];
        return;
      }

      const previousPosition = currentCluster[currentCluster.length - 1];
      if (position.x - previousPosition.x < MIN_SPACING) {
        currentCluster.push(position);
        return;
      }

      flushCluster();
      currentCluster = [position];
    });

    flushCluster();

    return items;
  }, [canEdit, resolveX, visibleReferences, width]);

  useEffect(() => {
    if (!expandedClusterId) return;

    const clusterStillVisible = referenceRenderItems.some((item) => item.type === 'cluster' && item.id === expandedClusterId);
    if (!clusterStillVisible) {
      setExpandedClusterId(null);
    }
  }, [expandedClusterId, referenceRenderItems]);

  // Show if there are references OR if we can edit (to show the add button)
  if (referenceRenderItems.length === 0 && !canEdit) return null;

  return (
    <div
      ref={containerRef}
      className="relative"
      style={{
        width,
        height: ORB_SIZE + 8,
        marginTop: 4,
        cursor: draggingRef ? 'grabbing' : undefined,
        touchAction: canEdit ? 'none' : 'auto',
      }}
    >
      {/* Add Reference button that follows playhead */}
      {canEdit && onAddReference && duration > 0 && (
        <motion.div
          data-timeline-pan-ignore="true"
          className="absolute cursor-pointer z-30"
          style={{
            left: `${Math.max(0, Math.min(width - ORB_SIZE, addButtonX - ORB_SIZE / 2))}px`,
            top: '4px'
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{
            scale: isHoveringAddButton ? 1.15 : 1,
            opacity: 1
          }}
          whileTap={{ scale: 0.95 }}
          onMouseEnter={() => setIsHoveringAddButton(true)}
          onMouseLeave={() => setIsHoveringAddButton(false)}
          onClick={() => {
            setExpandedClusterId(null);
            onAddReference(currentTime);
          }}
        >
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center text-[#232E3F] shadow-lg transition-all ring-1 ring-white/50"
            style={{
              backgroundColor: '#FFB52B',
              boxShadow: isHoveringAddButton
                ? '0 0 12px 4px rgba(255, 181, 43, 0.5)'
                : '0 0 8px 2px rgba(255, 181, 43, 0.32)',
            }}
          >
            <Plus size={12} strokeWidth={3} />
          </div>

          {/* Tooltip */}
          {isHoveringAddButton && (
            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 pointer-events-none z-50">
              <div
                className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-900'} text-white rounded-lg px-3 py-2 text-sm shadow-lg whitespace-nowrap`}
              >
                Add reference at current time
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                  <div
                    className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px]"
                    style={{ borderTopColor: isDarkMode ? '#1f2937' : '#111827' }}
                  />
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      <AnimatePresence>
        {referenceRenderItems.map((item) => {
          if (item.type === 'cluster') {
            const clusterCountLabel = item.references.length > 99 ? '99+' : String(item.references.length);
            const isOpen = expandedClusterId === item.id;
            const containsSelectedReference = item.references.some((reference) => reference.id === selectedReferenceId);
            const hasLinkedReference = item.references.some((reference) => !!reference.url);
            const isNearLeftEdge = item.x < CLUSTER_MENU_WIDTH / 2;
            const isNearRightEdge = item.x > width - CLUSTER_MENU_WIDTH / 2;

            return (
              <motion.div
                key={item.id}
                data-timeline-pan-ignore="true"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                whileHover={{ scale: 1.05 }}
                className="absolute z-20"
                style={{
                  left: `${item.x - CLUSTER_PILL_WIDTH / 2}px`,
                  top: '4px',
                  userSelect: 'none'
                }}
              >
                <button
                  type="button"
                  className={`
                    w-7 h-5 rounded-full flex items-center justify-center text-[#232E3F] shadow-lg transition-all
                    ${containsSelectedReference ? 'ring-2 ring-[#ffcf74] ring-offset-1' : ''}
                  `}
                  style={{
                    backgroundColor: '#FFB52B',
                    boxShadow: hasLinkedReference
                      ? '0 0 10px 3px rgba(255, 181, 43, 0.45)'
                      : '0 0 4px rgba(255, 181, 43, 0.28)',
                    opacity: hasLinkedReference ? 1 : 0.8
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedClusterId((currentClusterId) => currentClusterId === item.id ? null : item.id);
                  }}
                >
                  <span className="text-[10px] font-semibold leading-none">{clusterCountLabel}</span>
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 4, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 4, scale: 0.96 }}
                      transition={{ duration: 0.14 }}
                      className={`
                        absolute bottom-full mb-2 z-50
                        ${isNearLeftEdge ? 'left-0' : isNearRightEdge ? 'right-0' : 'left-1/2 -translate-x-1/2'}
                      `}
                      style={{ width: CLUSTER_MENU_WIDTH }}
                    >
                      <div
                        className="bg-[#232E3F] text-[#F4F4F4] rounded-lg shadow-xl border border-white/10 overflow-hidden"
                      >
                        <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-[#ffcf74] border-b border-white/10">
                          {item.references.length} references
                        </div>
                        <div className="max-h-64 overflow-y-auto py-1">
                          {item.references.map((reference) => {
                            const isSelected = selectedReferenceId === reference.id;

                            return (
                              <button
                                key={reference.id}
                                type="button"
                                className={`
                                  w-full text-left px-3 py-2 transition-colors
                                  ${isSelected ? 'bg-yellow-400/10' : 'hover:bg-white/5'}
                                `}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReferenceSelect(reference);
                                }}
                              >
                                <div className="flex items-start gap-2">
                                  <div className="mt-0.5 text-[#ffcf74]">
                                    {getCategoryIcon(reference)}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium truncate">
                                      {reference.title || reference.linkText || 'Untitled reference'}
                                    </div>
                                    <div className="text-[11px] opacity-70 mt-0.5">
                                      {formatReferenceTime(reference.start)}
                                      {reference.category ? ` • ${reference.category}` : ''}
                                    </div>
                                    {reference.linkText && reference.linkText !== reference.title && (
                                      <div className="text-[11px] text-[#d9e2f1] truncate mt-0.5">
                                        {reference.linkText}
                                      </div>
                                    )}
                                  </div>
                                  {reference.url && (
                                    <span className="text-[#ffe2a6] mt-0.5">
                                      <ExternalLink size={11} />
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className={`absolute top-full -mt-px ${
                        isNearLeftEdge ? 'left-3' :
                        isNearRightEdge ? 'right-3' :
                        'left-1/2 transform -translate-x-1/2'
                      }`}>
                        <div
                          className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px]"
                          style={{ borderTopColor: isDarkMode ? '#1f2937' : '#111827' }}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          }

          const { reference, x } = item;
          const hasUrl = !!reference.url;
          const isSelected = selectedReferenceId === reference.id;
          const isDragging = draggingRef?.id === reference.id;

          // Use drag position if this reference is being dragged
          const displayX = isDragging ? dragX : x;

          // Calculate tooltip alignment based on position
          // Tooltip is ~275px wide (between 200-350), so half is ~137px
          const tooltipHalfWidth = 137;
          const isNearLeftEdge = displayX < tooltipHalfWidth;
          const isNearRightEdge = displayX > width - tooltipHalfWidth;

          // Determine tooltip alignment
          let tooltipAlign = 'center'; // default: centered
          let tooltipTransform = '-translate-x-1/2';

          if (isNearLeftEdge) {
            tooltipAlign = 'left';
            tooltipTransform = 'translate-x-0';
          } else if (isNearRightEdge) {
            tooltipAlign = 'right';
            tooltipTransform = '-translate-x-full';
          }

          return (
            <motion.div
              key={reference.id}
              data-timeline-pan-ignore="true"
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: isDragging ? 1.2 : 1,
                opacity: 1
              }}
              exit={{ scale: 0, opacity: 0 }}
              whileHover={!isDragging ? { scale: 1.15 } : undefined}
              className={`absolute z-20 group ${
                canEdit ? 'cursor-grab' : 'cursor-pointer'
              } ${isDragging ? 'cursor-grabbing z-40' : ''}`}
              style={{
                left: `${displayX - ORB_SIZE / 2}px`,
                top: '4px',
                userSelect: 'none'
              }}
              onClick={() => {
                // Only trigger click if not dragging
                if (!isDragging) {
                  handleReferenceSelect(reference);
                }
              }}
              onPointerDown={(e) => {
                if (canEdit) {
                  handleDragStart(e, reference, x);
                }
              }}
            >
              {/* References with URLs have stronger glow and full opacity; no URL = softer appearance */}
              <div
                className={`
                  w-5 h-5 rounded-full flex items-center justify-center text-[#232E3F] shadow-lg transition-all
                  ${isSelected ? 'ring-2 ring-[#ffcf74] ring-offset-1' : ''}
                  ${hasUrl ? 'ring-1 ring-white/30' : ''}
                  ${isDragging ? 'ring-2 ring-white ring-offset-1' : ''}
                `}
                style={{
                  backgroundColor: '#FFB52B',
                  boxShadow: isDragging
                    ? '0 0 16px 6px rgba(255, 181, 43, 0.65)'
                    : hasUrl
                      ? '0 0 10px 3px rgba(255, 181, 43, 0.42)'
                      : '0 0 4px rgba(255, 181, 43, 0.24)',
                  opacity: hasUrl || isDragging ? 1 : 0.6
                }}
              >
                {getCategoryIcon(reference)}
              </div>

              {/* Hover tooltip - dynamically positioned based on edge proximity */}
              {/* Hide tooltip while dragging */}
              {!isDragging && (
                <div className={`
                  absolute bottom-full mb-2
                  ${tooltipAlign === 'left' ? 'left-0' : tooltipAlign === 'right' ? 'right-0' : 'left-1/2'}
                  transform ${tooltipTransform}
                  pointer-events-none opacity-0 group-hover:opacity-100
                  transition-opacity duration-200 z-50
                `}>
                  <div
                    className="bg-[#232E3F] text-[#F4F4F4] rounded-lg px-3 py-2 text-sm shadow-lg border border-white/10"
                    style={{ minWidth: '200px', maxWidth: '350px' }}
                  >
                    {/* Title */}
                    <div className="font-semibold text-xs mb-1 flex items-center gap-1.5">
                      {hasUrl && (
                        <span className="text-[#ffcf74]">
                          <Link2 size={12} />
                        </span>
                      )}
                      {reference.title}
                    </div>

                    {/* Category if present */}
                    {reference.category && (
                      <div className="text-xs text-[#d7ba7a] mb-1">
                        {reference.category}
                      </div>
                    )}

                    {/* Context if present */}
                    {reference.context && (
                      <div className="text-xs opacity-80 mb-1">
                        {reference.context.length > 100
                          ? reference.context.slice(0, 100) + '...'
                          : reference.context
                        }
                      </div>
                    )}

                    {/* URL preview */}
                    {hasUrl && reference.linkText && (
                      <div className="text-xs text-[#d9e2f1] flex items-center gap-1 mt-1">
                        <ExternalLink size={10} />
                        {reference.linkText}
                      </div>
                    )}

                    {/* Drag hint for editable references */}
                    {canEdit && (
                      <div className="text-xs text-[#ffcf74] mt-2 pt-1 border-t border-white/10">
                        Drag to reposition
                      </div>
                    )}

                    {/* Tooltip arrow - positioned based on tooltip alignment */}
                    <div className={`absolute top-full -mt-px ${
                      tooltipAlign === 'left' ? 'left-3' :
                      tooltipAlign === 'right' ? 'right-3' :
                      'left-1/2 transform -translate-x-1/2'
                    }`}>
                      <div
                        className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px]"
                        style={{ borderTopColor: isDarkMode ? '#1f2937' : '#111827' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Timestamp tooltip during drag */}
      {draggingRef && (
        <TimestampTooltip
          time={dragTime}
          x={tooltipPosition.x}
          y={tooltipPosition.y}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default TimelineReferences;
